import {
  describeUnsatisfiableReasons,
  parseUnsatisfiableReasons,
  type UnsatisfiableReason
} from './transfer-feasibility';
import type { components, paths } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type TransferRecommendationResponse =
  components['schemas']['TransferRecommendationResponse'];
export type TransferRecommendationData = TransferRecommendationResponse['data'];
export type TransferRecommendation =
  TransferRecommendationData['transfers'][number];
export type RecommendedChip = TransferRecommendationData['recommendedChip'];

export type SolverProfilesResponse =
  components['schemas']['SolverProfilesResponse'];
export type SolverProfile = SolverProfilesResponse['data'][number];

export type TransferRecommendationQuery = NonNullable<
  paths['/users/me/recommendations/transfers']['get']['parameters']['query']
>;

export type FetchTransferRecommendationsOptions =
  TransferRecommendationQuery & {
    accessToken: string;
    signal?: AbortSignal;
  };

const GENERIC_TRANSFER_FAILURE_MESSAGE =
  'Failed to load transfer recommendations';

/** The only status the API documents for a VALIDATION_ERROR refusal. */
const HTTP_BAD_REQUEST = 400;
const VALIDATION_ERROR_CODE = 'VALIDATION_ERROR';

/**
 * A refused or failed transfer recommendation request. Carries the status and
 * error code for diagnosis, and, where the API explained itself, the reasons
 * the request can never be satisfied.
 */
export class TransferRecommendationsApiError extends Error {
  status: number;
  code?: string;
  /** The API's own explanation, when it supplied a non-empty one. */
  apiMessage?: string;
  unsatisfiableReasons: UnsatisfiableReason[];

  constructor(
    status: number,
    message: string,
    options: {
      code?: string;
      apiMessage?: string;
      unsatisfiableReasons?: UnsatisfiableReason[];
    } = {}
  ) {
    super(message);
    this.name = 'TransferRecommendationsApiError';
    this.status = status;
    this.code = options.code;
    this.apiMessage = options.apiMessage;
    this.unsatisfiableReasons = options.unsatisfiableReasons ?? [];
  }
}

/**
 * Whether the request was refused because of what was asked for rather than
 * because the service failed. Keyed on the API having supplied a reason we can
 * render, not on the status code, so a member is only told "this cannot be
 * done" when there is something to tell them.
 */
export const isUnsatisfiableTransferRequest = (
  candidate: unknown
): candidate is TransferRecommendationsApiError =>
  candidate instanceof TransferRecommendationsApiError &&
  candidate.unsatisfiableReasons.length > 0;

/**
 * Whether the API refused the request because of malformed input rather than
 * an unsatisfiable one: a 400 the client sent, not a 422 the request itself
 * earned. Both the status and the code are required, so a gateway or upstream
 * failure that happens to echo the code is still treated as an outage worth
 * retrying rather than as input the member can correct.
 */
export const isInvalidTransferRequest = (
  candidate: unknown
): candidate is TransferRecommendationsApiError =>
  candidate instanceof TransferRecommendationsApiError &&
  candidate.status === HTTP_BAD_REQUEST &&
  candidate.code === VALIDATION_ERROR_CODE;

/**
 * Either kind of refusal the same bad input will always repeat, so a retry
 * would only delay the explanation rather than change the outcome.
 */
export const isRefusedTransferRequest = (
  candidate: unknown
): candidate is TransferRecommendationsApiError =>
  isUnsatisfiableTransferRequest(candidate) ||
  isInvalidTransferRequest(candidate);

/**
 * The refusal in the member's terms, or undefined when the failure was not a
 * refusal the API explained. Undefined is the signal to keep the generic
 * failure copy, which is what every response from an API without this detail
 * produces.
 */
export const describeTransferRecommendationsRefusal = (
  candidate: unknown
): string | undefined => {
  if (isUnsatisfiableTransferRequest(candidate)) {
    return describeUnsatisfiableReasons(candidate.unsatisfiableReasons);
  }
  if (isInvalidTransferRequest(candidate)) {
    return candidate.apiMessage;
  }
  return undefined;
};

/**
 * Reads a failed transfers response into the error the UI understands. Shared
 * with the asynchronous run endpoints so a refusal reads the same whichever
 * endpoint reported it.
 */
export const toTransferRecommendationsError = (
  status: number,
  body: unknown
): TransferRecommendationsApiError => {
  const errorBody = (
    body as { error?: { code?: string; message?: string } } | undefined
  )?.error;
  // An empty string is not a message either: `||` catches that in addition
  // to null/undefined so an empty-message VALIDATION_ERROR still falls back
  // to the generic copy instead of rendering blank.
  const apiMessage = errorBody?.message || undefined;

  return new TransferRecommendationsApiError(
    status,
    apiMessage ?? GENERIC_TRANSFER_FAILURE_MESSAGE,
    {
      code: errorBody?.code,
      apiMessage,
      unsatisfiableReasons: parseUnsatisfiableReasons(body)
    }
  );
};

export const fetchTransferRecommendations = async ({
  accessToken,
  signal,
  ...query
}: FetchTransferRecommendationsOptions): Promise<TransferRecommendationResponse> => {
  const { data, error, response } = await ffhClient().GET(
    '/users/me/recommendations/transfers',
    {
      headers: authHeader(accessToken),
      params: { query },
      signal
    }
  );

  if (error || !data) {
    throw toTransferRecommendationsError(response?.status ?? 0, error);
  }

  return data;
};

export const fetchSolverProfiles = async (
  accessToken: string
): Promise<SolverProfilesResponse> => {
  const { data, error } = await ffhClient().GET('/recommendations/profiles', {
    headers: authHeader(accessToken)
  });
  if (error) throw new Error('Failed to load solver profiles');
  return data;
};
